import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { tagopsService } from '../services/tagopsService';
import { useNotifications, createErrorNotification, createSuccessNotification } from './common/NotificationSystem';
import { useConfig } from '../contexts/ConfigContext';
import { PermissionButton } from './common/PermissionButton';

interface NamingConventionAccountAttachmentProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ruleName?: string;
}

export const NamingConventionAccountAttachment: React.FC<NamingConventionAccountAttachmentProps> = ({
  open,
  onClose,
  onSuccess,
  ruleName,
}) => {
  const { showNotification } = useNotifications();
  const { accountsData } = useConfig();
  const [loading, setLoading] = useState(false);
  const [attaching, setAttaching] = useState<string | null>(null);
  const [detaching, setDetaching] = useState<string | null>(null);
  const [accountRules, setAccountRules] = useState<Record<string, string | null>>({});
  const [availableRules, setAvailableRules] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rulesResponse, attachmentsResponse] = await Promise.all([
        tagopsService.listNamingConventions(),
        tagopsService.listNamingConventionAttachments(),
      ]);

      setAvailableRules(rulesResponse.naming_conventions || []);
      
      // Build account -> rule mapping
      const accountToRule: Record<string, string | null> = {};
      const accounts = accountsData?.accounts || [];
      
      // Initialize all accounts with null
      accounts.forEach((account) => {
        accountToRule[account.accountId] = null;
      });

      // Map attachments to accounts
      Object.entries(attachmentsResponse.naming_convention_attachments || {}).forEach(
        ([ruleName, accountIds]) => {
          accountIds.forEach((accountId) => {
            accountToRule[accountId] = ruleName;
          });
        }
      );

      setAccountRules(accountToRule);
    } catch (error: any) {
      console.error('Error loading attachment data:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to load attachment data'));
    } finally {
      setLoading(false);
    }
  };

  const handleAttach = async (accountId: string, selectedRule: string) => {
    try {
      setAttaching(accountId);
      
      // If account already has a rule, detach it first
      if (accountRules[accountId]) {
        await tagopsService.detachNamingConventionFromAccount(accountId);
      }
      
      await tagopsService.attachNamingConventionToAccount(selectedRule, accountId);
      showNotification(createSuccessNotification('Success', `Rule "${selectedRule}" attached to account ${accountId}`));
      
      // Update local state
      setAccountRules((prev) => ({
        ...prev,
        [accountId]: selectedRule,
      }));
      
      onSuccess();
    } catch (error: any) {
      console.error('Error attaching rule:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to attach rule'));
    } finally {
      setAttaching(null);
    }
  };

  const handleDetach = async (accountId: string) => {
    try {
      setDetaching(accountId);
      await tagopsService.detachNamingConventionFromAccount(accountId);
      showNotification(createSuccessNotification('Success', `Rule detached from account ${accountId}`));
      
      // Update local state
      setAccountRules((prev) => ({
        ...prev,
        [accountId]: null,
      }));
      
      onSuccess();
    } catch (error: any) {
      console.error('Error detaching rule:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to detach rule'));
    } finally {
      setDetaching(null);
    }
  };

  const handleRuleChange = (accountId: string, newRule: string) => {
    if (newRule === '') {
      // Detach if empty selection
      handleDetach(accountId);
    } else {
      // Attach new rule
      handleAttach(accountId, newRule);
    }
  };

  const accounts = accountsData?.accounts || [];
  // When viewing a specific rule, show ALL accounts (not just attached ones)
  // This allows users to see and add accounts that aren't attached yet
  const filteredAccounts = accounts;

  // When viewing a specific rule, show which accounts are attached to it
  const getAttachedAccountsForRule = (rule: string): string[] => {
    return Object.entries(accountRules)
      .filter(([_, attachedRule]) => attachedRule === rule)
      .map(([accountId]) => accountId);
  };

  const attachedAccountIds = ruleName ? getAttachedAccountsForRule(ruleName) : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {ruleName ? `Manage Attachments for "${ruleName}"` : 'Manage Account Attachments'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredAccounts.length === 0 ? (
          <Alert severity="info">No accounts available.</Alert>
        ) : (
          <>
            {ruleName && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {attachedAccountIds.length > 0
                  ? `${attachedAccountIds.length} account${attachedAccountIds.length !== 1 ? 's' : ''} attached to this rule.`
                  : 'No accounts attached to this rule yet. Use the "Attach" button to add accounts.'}
              </Alert>
            )}
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Account ID</strong></TableCell>
                    <TableCell><strong>Account Name</strong></TableCell>
                    {!ruleName && <TableCell><strong>Attached Rule</strong></TableCell>}
                    {ruleName && <TableCell><strong>Status</strong></TableCell>}
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAccounts.map((account) => {
                    const currentRule = accountRules[account.accountId];
                    const isAttachedToCurrentRule = ruleName ? currentRule === ruleName : false;
                    const isAttaching = attaching === account.accountId;
                    const isDetaching = detaching === account.accountId;
                    const isProcessing = isAttaching || isDetaching;
                    
                    return (
                      <TableRow 
                        key={account.accountId} 
                        hover
                        sx={{
                          bgcolor: ruleName && isAttachedToCurrentRule ? 'action.selected' : 'inherit',
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {account.accountId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {account.accountName || account.account_name || 'N/A'}
                          </Typography>
                        </TableCell>
                        {!ruleName && (
                          <TableCell>
                            {currentRule ? (
                              <Chip label={currentRule} size="small" color="primary" />
                            ) : (
                              <Chip label="Not Attached" size="small" variant="outlined" />
                            )}
                          </TableCell>
                        )}
                        {ruleName && (
                          <TableCell>
                            {isAttachedToCurrentRule ? (
                              <Chip label="Attached" size="small" color="primary" />
                            ) : currentRule ? (
                              <Chip 
                                label={`Attached to "${currentRule}"`} 
                                size="small" 
                                color="warning"
                              />
                            ) : (
                              <Chip label="Not Attached" size="small" variant="outlined" />
                            )}
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            {ruleName ? (
                              // When viewing a specific rule, show Attach/Detach buttons
                              isAttachedToCurrentRule ? (
                                <Tooltip title="Detach from this rule">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDetach(account.accountId)}
                                      disabled={isProcessing}
                                      color="error"
                                    >
                                      {isDetaching ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <LinkOffIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : (
                                <Tooltip title={currentRule ? `Detach from "${currentRule}" and attach to "${ruleName}"` : `Attach to "${ruleName}"`}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAttach(account.accountId, ruleName)}
                                      disabled={isProcessing}
                                      color="primary"
                                    >
                                      {isAttaching ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <AddIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )
                            ) : (
                              // When viewing all rules, show dropdown selector
                              <>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                  <Select
                                    value={currentRule || ''}
                                    onChange={(e) => handleRuleChange(account.accountId, e.target.value)}
                                    disabled={isProcessing}
                                    displayEmpty
                                  >
                                    <MenuItem value="">
                                      <em>None</em>
                                    </MenuItem>
                                    {availableRules.map((rule) => (
                                      <MenuItem key={rule} value={rule}>
                                        {rule}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {currentRule && (
                                  <Tooltip title="Detach Rule">
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDetach(account.accountId)}
                                        disabled={isProcessing}
                                        color="error"
                                      >
                                        {isDetaching ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <LinkOffIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
